import type { MemberStatus } from '@shared/types';

/** Flat draft: every editable field held as a string for controlled inputs.
 *  The worker trims, coerces card_no, and maps "" → null on submit. */
export type MemberDraft = Record<string, string>;

const TEXT = 'text' as const;
const DATE = 'date' as const;

interface FieldDef {
  name: string;
  label: string;
  type?: typeof TEXT | typeof DATE;
}

const SECTIONS: { title: string; fields: FieldDef[] }[] = [
  {
    title: 'Identity',
    fields: [
      { name: 'first_name', label: 'First name' },
      { name: 'last_name', label: 'Last name' },
      { name: 'aka', label: 'AKA' },
      { name: 'sex', label: 'Sex' },
      { name: 'dob', label: 'DOB', type: DATE },
      { name: 'card_no', label: 'Card #' },
    ],
  },
  {
    title: 'Contact',
    fields: [
      { name: 'address', label: 'Address' },
      { name: 'city', label: 'City' },
      { name: 'state', label: 'State' },
      { name: 'zip', label: 'Zip' },
      { name: 'phone', label: 'Phone' },
      { name: 'cell', label: 'Cell' },
      { name: 'unlisted_phone', label: 'Unlisted phone' },
      { name: 'pager', label: 'Pager' },
      { name: 'email', label: 'Email' },
    ],
  },
  {
    title: 'Membership',
    fields: [
      // status handled separately (select)
      { name: 'joined', label: 'Joined', type: DATE },
      { name: 'reactive', label: 'Reactivated' },
      { name: 'inactive', label: 'Inactive' },
      { name: 'resigned', label: 'Resigned' },
      { name: 'drop_date', label: 'Dropped', type: DATE },
      { name: 'reason_inactive', label: 'Reason inactive' },
    ],
  },
  {
    title: 'Operational',
    fields: [
      { name: 'battalion', label: 'Battalion' },
      { name: 'source', label: 'Source' },
      { name: 'area_interest', label: 'Area of interest' },
      { name: 'fingerprint', label: 'Fingerprint' },
      { name: 'chest_xray', label: 'Chest x-ray' },
    ],
  },
];

const inputClass =
  'w-full rounded-lg border border-border-default bg-surface-raised px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-dcvfd-accent';
const labelClass = 'text-xs uppercase tracking-wide text-zinc-400 mb-1 block';

interface Props {
  draft: MemberDraft;
  onChange: (name: string, value: string) => void;
  statuses: MemberStatus[];
}

export default function MemberFormFields({ draft, onChange, statuses }: Props) {
  return (
    <div className="space-y-5">
      {SECTIONS.map((section) => (
        <div key={section.title}>
          <h3 className="text-sm font-semibold text-dcvfd-dark mb-2">{section.title}</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-3">
            {section.title === 'Membership' && (
              <div>
                <label className={labelClass} htmlFor="status">
                  Status
                </label>
                <select
                  id="status"
                  value={draft.status ?? ''}
                  onChange={(e) => onChange('status', e.target.value)}
                  className={inputClass}
                >
                  <option value="">—</option>
                  {statuses.map((s) => (
                    <option key={s.code} value={s.code}>
                      {s.code}
                    </option>
                  ))}
                </select>
              </div>
            )}
            {section.fields.map((f) => (
              <div key={f.name}>
                <label className={labelClass} htmlFor={f.name}>
                  {f.label}
                </label>
                <input
                  id={f.name}
                  type={f.type ?? TEXT}
                  value={draft[f.name] ?? ''}
                  onChange={(e) => onChange(f.name, e.target.value)}
                  className={inputClass}
                />
              </div>
            ))}
          </div>
        </div>
      ))}
      <div>
        <label className={labelClass} htmlFor="comments">
          Comments
        </label>
        <textarea
          id="comments"
          value={draft.comments ?? ''}
          onChange={(e) => onChange('comments', e.target.value)}
          rows={3}
          className={inputClass}
        />
      </div>
    </div>
  );
}
